import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import { Toaster } from "sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Home from "./pages/Home";

// Découpage du code par page : chaque écran est chargé à la demande
const pageLoaders = {
  Trips: () => import("./pages/Trips"),
  Booking: () => import("./pages/Booking"),
  Parcel: () => import("./pages/Parcel"),
  Drivers: () => import("./pages/Drivers"),
  Rental: () => import("./pages/Rental"),
  Login: () => import("./pages/Login"),
  Register: () => import("./pages/Register"),
  ForgotPassword: () => import("./pages/ForgotPassword"),
  ResetPassword: () => import("./pages/ResetPassword"),
  Account: () => import("./pages/Account"),
  Admin: () => import("./pages/Admin"),
  DriverDashboard: () => import("./pages/DriverDashboard"),
  PublishTrip: () => import("./pages/PublishTrip"),
  Privacy: () => import("./pages/Privacy"),
  Terms: () => import("./pages/Terms"),
  Reservations: () => import("./pages/Reservations"),
  MyParcels: () => import("./pages/MyParcels"),
  Notifications: () => import("./pages/Notifications"),
  Help: () => import("./pages/Help"),
  About: () => import("./pages/About"),
};
const Trips = lazy(pageLoaders.Trips);
const Booking = lazy(pageLoaders.Booking);
const Parcel = lazy(pageLoaders.Parcel);
const Drivers = lazy(pageLoaders.Drivers);
const Rental = lazy(pageLoaders.Rental);
const Login = lazy(pageLoaders.Login);
const Register = lazy(pageLoaders.Register);
const ForgotPassword = lazy(pageLoaders.ForgotPassword);
const ResetPassword = lazy(pageLoaders.ResetPassword);
const Account = lazy(pageLoaders.Account);
const Admin = lazy(pageLoaders.Admin);
const DriverDashboard = lazy(pageLoaders.DriverDashboard);
const PublishTrip = lazy(pageLoaders.PublishTrip);
const Privacy = lazy(pageLoaders.Privacy);
const Terms = lazy(pageLoaders.Terms);
const Reservations = lazy(pageLoaders.Reservations);
const MyParcels = lazy(pageLoaders.MyParcels);
const Notifications = lazy(pageLoaders.Notifications);
const Help = lazy(pageLoaders.Help);
const About = lazy(pageLoaders.About);

const PREFETCH = ["Trips", "Booking", "Reservations", "Account", "Login", "Parcel", "Help"];

function usePrefetchMainPages() {
  useEffect(() => {
    const run = () => PREFETCH.forEach((k) => pageLoaders[k]().catch(() => {}));
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
    const id = idle(run);
    return () => (window.cancelIdleCallback || clearTimeout)(id);
  }, []);
}

import BottomNav from "./components/BottomNav";
import { OfflineScreen, useOnline, PageLoader } from "./components/Feedback";

function App() {
  const online = useOnline();
  usePrefetchMainPages();
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Header />
          <main className="pb-20 md:pb-0 safe-bottom">
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/trajets" element={<Trips />} />
              <Route path="/colis" element={<Parcel />} />
              <Route path="/location" element={<Rental />} />
              <Route path="/chauffeurs" element={<Drivers />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/confidentialite" element={<Privacy />} />
              <Route path="/conditions" element={<Terms />} />
              <Route path="/aide" element={<Help />} />
              <Route path="/a-propos" element={<About />} />
              <Route path="/reserver/:tripId" element={<ProtectedRoute><Booking /></ProtectedRoute>} />
              <Route path="/compte" element={<ProtectedRoute><Account /></ProtectedRoute>} />
              <Route path="/reservations" element={<ProtectedRoute><Reservations /></ProtectedRoute>} />
              <Route path="/mes-colis" element={<ProtectedRoute><MyParcels /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute role="admin"><Admin /></ProtectedRoute>} />
              <Route path="/chauffeur" element={<ProtectedRoute role="driver"><DriverDashboard /></ProtectedRoute>} />
              <Route path="/chauffeur/courses" element={<ProtectedRoute role="driver"><DriverDashboard view="courses" /></ProtectedRoute>} />
              <Route path="/chauffeur/publier" element={<ProtectedRoute role="driver"><PublishTrip /></ProtectedRoute>} />
              <Route path="/chauffeur/trajets/:tripId/modifier" element={<ProtectedRoute role="driver"><PublishTrip /></ProtectedRoute>} />
              <Route path="/chauffeur/locations" element={<ProtectedRoute role="driver"><DriverDashboard view="locations" /></ProtectedRoute>} />
            </Routes>
            </Suspense>
          </main>
          <Footer />
          <BottomNav />
          {!online && <OfflineScreen />}
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
